import {
    faBan,
    faCloudDownload,
    faCloudUpload,
    faMagnifyingGlass,
    faPause,
    faPlay,
    faTimes
} from "@fortawesome/pro-solid-svg-icons";
import {
    ActionIcon,
    AspectRatio,
    Box,
    Button,
    ButtonGroup,
    DataList,
    Divider,
    FileInput,
    Group,
    Modal,
    Progress,
    Tooltip,
    type FileInputProps,
    type MantineSize
} from "@mantine/core";
import { useDisclosure, useId } from "@mantine/hooks";
import type { ValidationError } from "@ngt-form/core";
import { useMantineFormOptions } from "@ngt-form/mantine";
import dayjs from "dayjs";
import { useMemo, type FC } from "react";

import { FileState, type TemporaryFileInformation } from "src/api/Tmd.dtos";
import Icon from "src/components/common/Icon";

import type { FileUploadInputExistingFile, FileUploadInputViewProps } from "./FileUploadInput";
import classes from "./FiledUploadInput.DefaultView.module.css";

/**
 * Formats a byte count using decimal units and the existing precision fallback.
 * Postconditions: Loose zero equality returns 0 Bytes; falsy decimals use two places.
 * Assumptions: Callers provide a supported nonnegative count and valid precision. Invalid inputs are
 * not normalized; arithmetic/formatting behavior, including possible errors, is retained.
 */
export function formatBytes(bytes: number, decimals: number) {
    if (bytes == 0) {
        return "0 Bytes";
    }

    var k = 1000,
        dm = decimals || 2,
        sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
        i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

type FileUploadInputDefaultViewProps<
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
> = FileUploadInputViewProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
> & {};

type InformationViewProps = {
    temporaryFileInformation?: TemporaryFileInformation | null | undefined;
    existingFileInformation?: FileUploadInputExistingFile | null | undefined;
};

export type FileUploadInformationProps = {
    size?: string | null | undefined;
    name: string | null | undefined;
    contentType: string | null | undefined;
    image?: boolean | null | undefined;
    date?: string | null | undefined;
    url?: string | null | undefined;
    downloadUrl?: string | null | undefined;
};

/**
 * Shows supplied file metadata in a modal with optional download and image preview controls.
 * Postconditions: Only truthy metadata rows render. Download requires a URL; image preview requires
 * both image and URL. Opening requests local UI state and performs no permission check here.
 */
export const FileUploadInformation: FC<FileUploadInformationProps> = ({
    contentType,
    name,
    date,
    downloadUrl,
    image,
    size,
    url
}) => {
    const mantineFormOptions = useMantineFormOptions();
    const [opened, openedHandlers] = useDisclosure();

    return (
        <>
            <ActionIcon.Group>
                <Tooltip label="File information">
                    <ActionIcon
                        size={`input-${mantineFormOptions.inputs.defaultSize}`}
                        onClick={openedHandlers.open}
                    >
                        <Icon icon={faMagnifyingGlass} />
                    </ActionIcon>
                </Tooltip>
                {downloadUrl && (
                    <Tooltip label="Download file">
                        <ActionIcon
                            size={`input-${mantineFormOptions.inputs.defaultSize}`}
                            component="a"
                            href={downloadUrl}
                            target="_blank"
                        >
                            <Icon icon={faCloudDownload} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </ActionIcon.Group>
            <Modal
                opened={opened}
                onClose={openedHandlers.close}
                title="File Information"
                styles={{
                    body: {
                        padding: 0
                    }
                }}
            >
                <Divider />
                <DataList
                    orientation="horizontal"
                    withDivider
                    py="xs"
                >
                    {name && (
                        <DataList.Item px="md">
                            <DataList.ItemLabel>File Name</DataList.ItemLabel>
                            <DataList.ItemValue>{name}</DataList.ItemValue>
                        </DataList.Item>
                    )}
                    {size && (
                        <DataList.Item px="md">
                            <DataList.ItemLabel>File Size</DataList.ItemLabel>
                            <DataList.ItemValue>{size}</DataList.ItemValue>
                        </DataList.Item>
                    )}
                    {contentType && (
                        <DataList.Item px="md">
                            <DataList.ItemLabel>Content Type</DataList.ItemLabel>
                            <DataList.ItemValue>{contentType}</DataList.ItemValue>
                        </DataList.Item>
                    )}
                    {date && (
                        <DataList.Item px="md">
                            <DataList.ItemLabel>Date Uploaded</DataList.ItemLabel>
                            <DataList.ItemValue>{date}</DataList.ItemValue>
                        </DataList.Item>
                    )}
                </DataList>
                <Divider />
                {image && url && (
                    <Group
                        m="md"
                        justify="center"
                    >
                        <img
                            style={{ maxWidth: "100%", maxHeight: "100%" }}
                            src={url}
                            alt="Uploaded Image"
                        />
                    </Group>
                )}
            </Modal>
        </>
    );
};

/**
 * Projects successful temporary-file metadata or the existing source file into information controls.
 * Postconditions: A successful temporary value needs metadata to take precedence; otherwise existing
 * file data is used. No metadata source renders nothing; existing format and route rules are retained.
 */
const InformationView: FC<InformationViewProps> = ({
    existingFileInformation,
    temporaryFileInformation
}) => {
    /**
     * Builds display/download metadata with temporary-file precedence and Day.js date formatting.
     */
    const fileInformation = useMemo(
        () => {
            const hasTempFile =
                temporaryFileInformation &&
                temporaryFileInformation.state === FileState.Success &&
                !!temporaryFileInformation.temporaryFile;
            const hasSourceFile = !!existingFileInformation;

            if (!hasTempFile && !hasSourceFile) {
                return undefined;
            }

            if (hasTempFile) {
                return {
                    size: formatBytes(temporaryFileInformation.temporaryFile.fileSize, 2),
                    name: temporaryFileInformation.temporaryFile.name,
                    contentType: temporaryFileInformation.temporaryFile.contentType,
                    image: temporaryFileInformation.temporaryFile.image,
                    date: temporaryFileInformation.temporaryFile.dateUploaded
                        ? dayjs(temporaryFileInformation.temporaryFile.dateUploaded).format(
                              "DD/MM/YYYY"
                          )
                        : undefined,
                    url: `/api/v2/temporary-file/${temporaryFileInformation.temporaryFile.id}`,
                    downloadUrl: `/api/v2/temporary-file/download/${temporaryFileInformation.temporaryFile.id}`
                };
            }

            return {
                size: formatBytes(existingFileInformation.fileSize, 2),
                name: existingFileInformation.name,
                contentType: existingFileInformation.contentType,
                image: existingFileInformation.image,
                date: existingFileInformation.dateUploaded
                    ? dayjs(existingFileInformation.dateUploaded).format("DD/MM/YYYY")
                    : undefined,
                url: `/api/v2/source-document/${existingFileInformation.id}`,
                downloadUrl: `/api/v2/source-document/download/${existingFileInformation.id}`
            };
        },
        [temporaryFileInformation, existingFileInformation]
    );

    if (!fileInformation) {
        return null;
    }

    return <FileUploadInformation {...fileInformation} />;
};

/**
 * Offers file replacement alongside existing/current file information.
 * Postconditions: Selection delegates the first file or undefined without awaiting upload. Focus/blur
 * update form flags before callbacks. This view retains its existing lack of disabled control props.
 */
const ReplaceView = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    fieldInput: {
        validationErrorTypeName,
        label,
        actions,
        disabled,
        validationErrors,
        validationResult,
        snapshot,
        convertValue
    },
    file,
    existingFile,
    upload,
    onFocus,
    onBlur
}: FileUploadInputDefaultViewProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    const mantineFormOptions = useMantineFormOptions();

    const id = useId();

    return (
        <Group>
            <Box flex="1 1 auto">
                <Tooltip label="Replace with another file">
                    <Button
                        component="label"
                        htmlFor={id}
                        fullWidth
                        leftSection={<Icon icon={faCloudUpload} />}
                        size={mantineFormOptions.inputs.defaultSize}
                        onBlur={e => {
                            actions.setFocused(false, { notify: true, updateTouched: true });
                            onBlur?.(e, actions);
                        }}
                        onFocus={e => {
                            actions.setFocused(true, { notify: true, updateTouched: false });
                            onFocus?.(e, actions);
                        }}
                    >
                        Replace
                    </Button>
                </Tooltip>
                <input
                    id={id}
                    type="file"
                    multiple={false}
                    style={{
                        zIndex: -1,
                        position: "absolute"
                    }}
                    onChange={e => {
                        if (e.target.files && e.target.files.length !== 0) {
                            upload(e.target.files[0]);
                        } else {
                            upload(undefined);
                        }
                    }}
                />
            </Box>
            <InformationView
                existingFileInformation={existingFile}
                temporaryFileInformation={convertValue(snapshot.value)}
            />
        </Group>
    );
};

/**
 * Offers removal of a successful temporary upload while retaining file information.
 * Postconditions: Remove delegates undefined without awaiting upload; form focus/touch callbacks keep
 * their existing order. This view does not itself delete persisted source documents.
 */
const CancelView = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    fieldInput: {
        validationErrorTypeName,
        label,
        actions,
        disabled,
        validationErrors,
        validationResult,
        snapshot,
        convertValue
    },
    file,
    existingFile,
    upload,
    onFocus,
    onBlur
}: FileUploadInputDefaultViewProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    const mantineFormOptions = useMantineFormOptions();

    return (
        <Group>
            <Box flex="1 1 auto">
                <Tooltip label="Remove file upload">
                    <Button
                        fullWidth
                        leftSection={<Icon icon={faTimes} />}
                        size={mantineFormOptions.inputs.defaultSize}
                        onBlur={e => {
                            actions.setFocused(false, { notify: true, updateTouched: true });
                            onBlur?.(e, actions);
                        }}
                        onFocus={e => {
                            actions.setFocused(true, { notify: true, updateTouched: false });
                            onFocus?.(e, actions);
                        }}
                        onClick={() => {
                            upload(undefined);
                        }}
                    >
                        Remove
                    </Button>
                </Tooltip>
            </Box>
            <InformationView
                existingFileInformation={existingFile}
                temporaryFileInformation={convertValue(snapshot.value)}
            />
        </Group>
    );
};

const progressSize: Record<MantineSize, string> = {
    xs: "calc(1.875rem * var(--mantine-scale))",
    sm: "calc(2.25rem * var(--mantine-scale))",
    md: "calc(2.625rem * var(--mantine-scale))",
    lg: "calc(3.125rem * var(--mantine-scale))",
    xl: "calc(3.75rem * var(--mantine-scale))"
};

/**
 * Shows chunk progress with pause/resume/cancel controls when an upload ID exists.
 * Postconditions: Cancelling disables actions; missing progress displays a full indeterminate bar.
 * Cancel failures are caught and logged; pause/resume promises remain fire-and-forget.
 */
const ChunkedUploadingView = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    chunkedUploader,
    uploadState,
    file
}: FileUploadInputDefaultViewProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    const mantineFormOptions = useMantineFormOptions();

    const height = progressSize[mantineFormOptions.inputs.defaultSize];
    const uploadId = uploadState?.uploadId;
    if (!uploadId) {
        return null;
    }

    const progress = uploadState.progress;
    const progressLabel = progress === undefined ? "Uploading..." : `${progress}%`;
    const isPaused = uploadState.state === "paused";
    const isCancelling = uploadState.state === "cancelling";
    const isUploadActionDisabled = isCancelling;

    return (
        <Group>
            <Box
                flex="1 1 auto"
                pos="relative"
            >
                <Progress.Root
                    h={height}
                    autoContrast
                >
                    <Progress.Section
                        value={progress ?? 100}
                        animated={!isPaused && !isCancelling}
                        striped={isPaused || isCancelling}
                    ></Progress.Section>
                </Progress.Root>
                <Group
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    pos="absolute"
                    align="center"
                    justify="center"
                >
                    {isCancelling ? "Cancelling..." : isPaused ? "Paused" : progressLabel}
                </Group>
                <Group
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    pos="absolute"
                    align="center"
                    justify="center"
                    c="white"
                    style={{
                        overflow: "hidden",
                        clipPath: `inset(0 calc(100% - var(--progress, ${progress ?? 100}%)) 0 0)`
                    }}
                >
                    {isCancelling ? "Cancelling..." : isPaused ? "Paused" : progressLabel}
                </Group>
            </Box>
            <ActionIcon.Group>
                {isPaused ? (
                    <Tooltip
                        label={isCancelling ? "File upload is cancelling" : "Resume file upload"}
                    >
                        <ActionIcon
                            size={`input-${mantineFormOptions.inputs.defaultSize}`}
                            disabled={isUploadActionDisabled}
                            onClick={() => {
                                void chunkedUploader.upload(file, uploadId);
                            }}
                        >
                            <Icon icon={faPlay} />
                        </ActionIcon>
                    </Tooltip>
                ) : (
                    <Tooltip
                        label={isCancelling ? "File upload is cancelling" : "Pause file upload"}
                    >
                        <ActionIcon
                            size={`input-${mantineFormOptions.inputs.defaultSize}`}
                            disabled={isUploadActionDisabled}
                            onClick={() => {
                                void chunkedUploader.pause(uploadId);
                            }}
                        >
                            <Icon icon={faPause} />
                        </ActionIcon>
                    </Tooltip>
                )}
                <Tooltip label="Cancel file upload">
                    <ActionIcon
                        size={`input-${mantineFormOptions.inputs.defaultSize}`}
                        disabled={isCancelling}
                        onClick={() => {
                            void chunkedUploader
                                .cancel(uploadId)
                                .catch(error =>
                                    console.error("Failed to cancel chunked upload:", error)
                                );
                        }}
                    >
                        <Icon icon={faBan} />
                    </ActionIcon>
                </Tooltip>
            </ActionIcon.Group>
        </Group>
    );
};

/**
 * Shows non-chunked progress without pause/cancel controls, retaining the indeterminate fallback.
 */
const UploadingView = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    uploadState
}: FileUploadInputDefaultViewProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    const mantineFormOptions = useMantineFormOptions();

    const height = progressSize[mantineFormOptions.inputs.defaultSize];
    const progress = uploadState?.progress;
    const progressLabel = progress === undefined ? "Uploading..." : `${progress}%`;

    return (
        <Box
            flex="1 1 auto"
            pos="relative"
        >
            <Progress.Root h={height}>
                <Progress.Section
                    value={progress ?? 100}
                    animated
                    c="white"
                >
                    {progressLabel}
                </Progress.Section>
            </Progress.Root>
            <Group
                top={0}
                left={0}
                right={0}
                bottom={0}
                pos="absolute"
                align="center"
                justify="center"
            >
                {progressLabel}
            </Group>
            <Group
                top={0}
                left={0}
                right={0}
                bottom={0}
                pos="absolute"
                align="center"
                justify="center"
                c="white"
                style={{
                    overflow: "hidden",
                    clipPath: `inset(0 calc(100% - var(--progress, ${progress ?? 100}%)) 0 0)`
                }}
            >
                {progressLabel}
            </Group>
        </Box>
    );
};

/**
 * Offers the initial single-file picker and delegates selection to upload.
 * Postconditions: Both trigger and input respect disabled. Focus/blur update field flags first;
 * selection forwards the first file or undefined without awaiting transfer completion.
 */
const UploadView = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    fieldInput: { actions },
    upload,
    onFocus,
    onBlur,
    disabled
}: FileUploadInputDefaultViewProps<
    TFormValue,
    TValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    const mantineFormOptions = useMantineFormOptions();

    const id = useId();

    return (
        <>
            <Tooltip label="Upload a file">
                <Button
                    disabled={disabled}
                    component="label"
                    htmlFor={id}
                    fullWidth
                    leftSection={<Icon icon={faCloudUpload} />}
                    size={mantineFormOptions.inputs.defaultSize}
                    onBlur={e => {
                        actions.setFocused(false, { notify: true, updateTouched: true });
                        onBlur?.(e, actions);
                    }}
                    onFocus={e => {
                        actions.setFocused(true, { notify: true, updateTouched: false });
                        onFocus?.(e, actions);
                    }}
                >
                    Upload
                </Button>
            </Tooltip>
            <input
                id={id}
                type="file"
                disabled={disabled}
                multiple={false}
                style={{
                    zIndex: -1,
                    position: "absolute"
                }}
                onChange={e => {
                    if (e.target.files && e.target.files.length !== 0) {
                        upload(e.target.files[0]);
                    } else {
                        upload(undefined);
                    }
                }}
            />
        </>
    );
};

/**
 * Chooses progress, replacement, initial-upload or successful-upload removal presentation.
 * Postconditions: Active upload metadata takes precedence; upload ID selects chunk controls. Empty
 * field state chooses replacement when an existing file exists, otherwise initial upload. Success
 * chooses removal; other field states retain the existing undefined result.
 */
const FileUploadInputDefaultView = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>(
    props: FileUploadInputDefaultViewProps<
        TFormValue,
        TValidationError,
        TFormMetadata,
        TFieldValue,
        TLookupItem
    >
) => {
    const value = props.fieldInput.convertValue(props.fieldInput.snapshot.value);
    const isUploading =
        props.uploadState?.state === "idle" ||
        props.uploadState?.state === "uploading" ||
        props.uploadState?.state === "paused" ||
        props.uploadState?.state === "cancelling";

    if (isUploading) {
        if (props.uploadState?.uploadId) {
            return <ChunkedUploadingView {...props} />;
        }

        return <UploadingView {...props} />;
    }

    if (!value || !value.state || value.state === FileState.Empty) {
        if (props.existingFile) {
            return <ReplaceView {...props} />;
        }

        return <UploadView {...props} />;
    }

    if (value.state === FileState.Success) {
        return <CancelView {...props} />;
    }
};

export default FileUploadInputDefaultView;
